<template>
    <div class="ticket-details-container">
        <!-- Header with Ticket ID and Actions -->
        <div class="ticket-header">
            <div class="ticket-id-section">
                <div class="ticket-badge">
                    <span class="badge-icon">🎫</span>
                    <span class="badge-text">TICKET</span>
                    <span class="badge-number">#{{ item.id || 'NEW' }}</span>
                </div>
                <div class="ticket-status-chip" :class="getStatusClass(item.status)">
                    <span class="status-dot"></span>
                    {{ getStatusLabel(item.status) }}
                </div>
            </div>
            <button class="save-button" @click="save">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polygon points="12 7 12 13 9 13 12 16 15 13 12 13 12 7" />
                    <line x1="9" y1="21" x2="9" y2="15" />
                </svg>
                Save Changes
            </button>
        </div>

        <!-- Main Content Grid -->
        <div class="ticket-grid">
            <!-- Left Column -->
            <div class="form-column">
                <!-- Basic Info Card -->
                <div class="form-card">
                    <div class="card-header">
                        <div class="header-icon">📋</div>
                        <h3>Basic Information</h3>
                    </div>
                    <div class="card-body">
                        <div class="form-row">
                            <div class="form-group">
                                <label>Invoice Number</label>
                                <div class="input-wrapper">
                                    <span class="input-icon">🧾</span>
                                    <input type="text" v-model="item.invoice_id" placeholder="INV-000001" />
                                </div>
                            </div>
                            <div class="form-group">
                                <label>Product SKU</label>
                                <div class="input-wrapper">
                                    <span class="input-icon">📦</span>
                                    <input type="text" v-model="item.product_sku" placeholder="SKU-XXXXX" />
                                </div>
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label>Claim Date</label>
                                <date-picker
                                    v-model="item.claim_date"
                                    format="MM-DD-YYYY"
                                    valueType="format"
                                    placeholder="Select date"
                                    class="custom-datepicker"
                                ></date-picker>
                            </div>
                            <div class="form-group">
                                <label>Status</label>
                                <div class="select-wrapper">
                                    <select v-model="item.status" @change="onStatusChange" class="custom-select">
                                        <option v-for="status in statuses" :key="status.value" :value="status.value">
                                            {{ status.text }}
                                        </option>
                                    </select>
                                    <span class="select-arrow">⌵</span>
                                </div>
                            </div>
                            <div class="form-group">
                                <label>Repair/Exchange Cost</label>
                                <div class="input-wrapper">
                                    <span class="input-icon">💰</span>
                                    <input type="number" v-model="item.fix_cost" placeholder="0.00" step="0.01" />
                                </div>
                            </div>
                        </div>

                        <div class="form-group full-width">
                            <label>Claim Details</label>
                            <textarea v-model="item.claim_detail" rows="3" placeholder="Describe the issue in detail..."></textarea>
                        </div>
                    </div>
                </div>

                <!-- Parts Information Card -->
                <div class="form-card">
                    <div class="card-header">
                        <div class="header-icon">🔧</div>
                        <h3>Parts Information</h3>
                    </div>
                    <div class="card-body">
                        <div class="form-row">
                            <div class="form-group">
                                <label>Parts Required?</label>
                                <div class="radio-group">
                                    <label class="radio-label">
                                        <input type="radio" v-model="item.parts_required" :value="true" @change="onPartsRequiredChange">
                                        <span>Yes</span>
                                    </label>
                                    <label class="radio-label">
                                        <input type="radio" v-model="item.parts_required" :value="false" @change="onPartsRequiredChange">
                                        <span>No</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Vendor Information Display -->
                        <div v-if="showVendorInfo" class="vendor-info-card">
                            <div class="vendor-header">
                                <span class="vendor-icon">🏭</span>
                                <strong>Manufacturer/Vendor Information</strong>
                            </div>
                            <div class="vendor-details">
                                <div class="vendor-row">
                                    <span class="vendor-label">Vendor:</span>
                                    <span class="vendor-value">{{ item.vendor_name }}</span>
                                </div>
                                <div class="vendor-row">
                                    <span class="vendor-label">Email:</span>
                                    <span class="vendor-value">
                                        <a :href="'mailto:' + item.vendor_email" class="vendor-email-link">
                                            {{ item.vendor_email }}
                                        </a>
                                    </span>
                                </div>
                                <div class="vendor-row" v-if="item.product_name">
                                    <span class="vendor-label">Product:</span>
                                    <span class="vendor-value">{{ item.product_name }}</span>
                                </div>
                            </div>
                        </div>

                        <div v-if="showPartsSection" class="parts-details">
                            <div class="form-row">
                                <div class="form-group">
                                    <label>Parts Status</label>
                                    <div class="select-wrapper">
                                        <select v-model="item.parts_status" class="custom-select" @change="onPartsStatusChange">
                                            <option v-for="status in parts_statuses" :key="status.value" :value="status.value">
                                                {{ status.text }}
                                            </option>
                                        </select>
                                        <span class="select-arrow">⌵</span>
                                    </div>
                                </div>
                            </div>

                            <div class="form-row">
                                <div class="form-group full-width">
                                    <label>Parts Details / Order Number</label>
                                    <textarea v-model="item.parts_details" rows="2" placeholder="Enter part numbers, quantities, or order details..."></textarea>
                                </div>
                            </div>

                            <!-- Conditional alerts based on parts status -->
                            <div v-if="item.parts_status === 'need_to_order'" class="alert-info">
                                <div class="alert-icon">📦</div>
                                <div class="alert-content">
                                    <strong>Order Required</strong>
                                    <p>Please place an order with the manufacturer and update the status once ordered.</p>
                                </div>
                            </div>

                            <div v-if="item.parts_status === 'ordered'" class="alert-warning">
                                <div class="alert-icon">🚚</div>
                                <div class="alert-content">
                                    <strong>Parts Ordered</strong>
                                    <p>Expected delivery: 5-7 business days. Update status when received.</p>
                                </div>
                            </div>

                            <div v-if="item.parts_status === 'received'" class="alert-success">
                                <div class="alert-icon">✅</div>
                                <div class="alert-content">
                                    <strong>Parts Received</strong>
                                    <p>Parts are ready for installation.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Action & Scheduling Card -->
                <div class="form-card">
                    <div class="card-header">
                        <div class="header-icon">⚡</div>
                        <h3>Action & Scheduling</h3>
                    </div>
                    <div class="card-body">
                        <div class="form-row">
                            <div class="form-group flex-2">
                                <label>Action Requested</label>
                                <div class="select-wrapper">
                                    <select v-model="item.action" class="custom-select">
                                        <option v-for="action in action_types" :key="action.value" :value="action.value">
                                            {{ action.text }}
                                        </option>
                                    </select>
                                    <span class="select-arrow">⌵</span>
                                </div>
                            </div>
                            <div class="form-group">
                                <label>Scheduled Date</label>
                                <date-picker
                                    v-model="item.scheduled_date"
                                    format="MM-DD-YYYY"
                                    valueType="format"
                                    placeholder="Select date"
                                    class="custom-datepicker"
                                    @change="onScheduledDateChange"
                                ></date-picker>
                            </div>
                        </div>

                        <div class="form-group full-width">
                            <label>Comments</label>
                            <textarea v-model="item.comments" rows="2" placeholder="Add internal notes or comments..."></textarea>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Column -->
            <div class="media-column">
                <!-- Images Card -->
                <div class="form-card">
                    <div class="card-header">
                        <div class="header-icon">🖼️</div>
                        <h3>Attachments</h3>
                        <label class="upload-button">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            Add Image
                            <input type="file" @change="previewImage" accept="image/*" hidden />
                        </label>
                    </div>
                    <div class="card-body">
                        <div class="gallery-section" v-if="item.images.length > 0">
                            <div class="image-grid">
                                <div v-for="(image, idx) in item.images" :key="idx" class="image-thumb" 
                                     @mouseenter="hovered_image = image" @mouseleave="hovered_image = {}">
                                    <img :src="image.raw || image.url" :alt="'Image ' + (idx+1)" />
                                    <button class="remove-image" @click.stop="removeImage(idx)">✕</button>
                                </div>
                            </div>
                        </div>
                        <div v-else class="empty-gallery">
                            <div class="empty-icon">📷</div>
                            <p>No images attached</p>
                            <span>Click "Add Image" to upload photos</span>
                        </div>

                        <!-- Image Preview Modal -->
                        <div v-if="hovered_image.raw || hovered_image.url" class="image-preview-card">
                            <div class="preview-header">
                                <span>🔍 Preview</span>
                            </div>
                            <img :src="hovered_image.raw || hovered_image.url" class="preview-image" />
                        </div>
                    </div>
                </div>

                <!-- Quick Actions Card -->
                <div class="form-card info-card">
                    <div class="card-header">
                        <div class="header-icon">ℹ️</div>
                        <h3>Quick Info</h3>
                    </div>
                    <div class="card-body">
                        <div class="info-row">
                            <span class="info-label">Created</span>
                            <span class="info-value">{{ formatDate(item.created_on) || '—' }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Last Updated</span>
                            <span class="info-value">{{ formatDate(item.updated_on) || '—' }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Assigned To</span>
                            <span class="info-value">{{ item.assigned_to || 'Unassigned' }}</span>
                        </div>
                        <div class="info-row" v-if="item.parts_required">
                            <span class="info-label">Parts Status</span>
                            <span class="info-value" :class="getPartsStatusClass(item.parts_status)">
                                {{ getPartsStatusLabel(item.parts_status) }}
                            </span>
                        </div>
                        <div class="divider"></div>
                        <div class="status-timeline" v-if="item.status_history && item.status_history.length">
                            <div class="timeline-title">Status History</div>
                            <div v-for="(history, idx) in item.status_history.slice(0, 3)" :key="idx" class="timeline-item">
                                <span class="timeline-dot"></span>
                                <span class="timeline-status">{{ history.status }}</span>
                                <span class="timeline-date">{{ formatShortDate(history.date) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment'
    import Requestor from '@/utilities/Requestor'
    import DatePicker from 'vue2-datepicker';
    import 'vue2-datepicker/index.css';

    export default {
        name: "CustomerServiceTicket",
        props: ["viewdata"],
        components: { DatePicker },
        data: function() {
            return {
                items_being_loaded: false,
                item: {
                    id: null,
                    invoice_id: null,
                    product_sku: null,
                    claim_date: null,
                    claim_detail: null,
                    images: [],
                    action: null,
                    scheduled_date: null,
                    comments: null,
                    status: "new",
                    fix_cost: null,
                    created_on: null,
                    updated_on: null,
                    assigned_to: null,
                    status_history: [],
                    // New parts fields
                    parts_required: false,
                    parts_status: null,
                    parts_details: null,
                    // Vendor fields
                    vendor_name: null,
                    vendor_email: null,
                    product_name: null
                },
                action_types: [
                    { value: "service", text: "Service" },
                    { value: "exchange", text: "Exchange" },
                    { value: "repair_at_ch", text: "Repair at Customer House" },
                    { value: "repair_at_wh", text: "Repair at Warehouse" },
                    { value: "order_parts_with_mfg", text: "Order Parts with Manufacturer" },
                    { value: "not_covered", text: "Not Covered" },
                    { value: "covered_under_extended_warranty", text: "Covered Under Extended Warranty" },
                ],
                statuses: [
                    { value: "new", text: "New" },
                    { value: "open", text: "Open" },
                    { value: "assigned", text: "Assigned" },
                    { value: "scheduled", text: "Scheduled" },
                    { value: "resolved", text: "Resolved" },
                    { value: "exchange", text: "Exchange" },
                    { value: "denied", text: "Denied" },
                    { value: "closed", text: "Closed" },
                    { value: "pending", text: "Pending" }
                ],
                parts_statuses: [
                    { value: "in_stock", text: "In Stock" },
                    { value: "need_to_order", text: "Need to Order" },
                    { value: "ordered", text: "Ordered" },
                    { value: "received", text: "Received" }
                ],
                hovered_image: {},
                newt: false,
            }
        },
        computed: {
            // Computed property for parts section - will update automatically when parts_required changes
            showPartsSection: function() {
                return this.item.parts_required === true;
            },
            // Computed property for vendor info section
            showVendorInfo: function() {
                return this.item.parts_required === true && this.item.vendor_name;
            }
        },
        watch: {
            'item.claim_date': {
                handler: function(newDate, oldDate) {
                    // Check if a valid date is set and the current status is not already 'open' or further along
                    if (newDate && newDate.trim() !== '' && 
                        (!oldDate || oldDate.trim() === '') && 
                        this.item.status !== 'open' && 
                        this.item.status !== 'scheduled' && 
                        this.item.status !== 'resolved' && 
                        this.item.status !== 'closed') {
                        
                        this.item.status = 'open';
                        this.$bvToast.toast("Claim date set. Status automatically updated to 'Open'.", { 
                            title: "Status Updated", 
                            variant: "success", 
                            autoHideDelay: 1500 
                        });
                    }
                },
                deep: true,
                immediate: false
            },
            // Watch for parts_required changes to help with debugging
            'item.parts_required': {
                handler: function(newVal) {
                    console.log('Parts required changed to:', newVal);
                },
                immediate: true
            }
        },
        methods: {
            
            previewImage: function(event) {
                var input = event.target;
                if (input.files && input.files[0]) {
                    let img = { raw: "", url: "", file: null }
                    var reader = new FileReader();
                    reader.onload = (e) => {
                        img.raw = e.target.result
                        this.item.images.push(img);
                    }
                    img = { raw: "", url: "", file: input.files[0] }
                    reader.readAsDataURL(input.files[0]);
                }
            },
            
            removeImage: function(index) {
                this.item.images.splice(index, 1);
                this.$bvToast.toast("Image removed", { title: "Info", variant: "info", autoHideDelay: 1500 });
            },
            
            onPartsRequiredChange: function() {
                console.log('onPartsRequiredChange triggered, current value:', this.item.parts_required);
                
                if (!this.item.parts_required) {
                    this.item.parts_status = null;
                    this.item.parts_details = null;
                    this.$bvToast.toast("Parts not required for this ticket.", { 
                        title: "Info", 
                        variant: "info", 
                        autoHideDelay: 1500 
                    });
                } else {
                    this.item.parts_status = "need_to_order";
                    this.$bvToast.toast("Parts required. Please fill in the parts details.", { 
                        title: "Info", 
                        variant: "info", 
                        autoHideDelay: 1500 
                    });
                }
                
                // Force Vue to update the DOM immediately
                this.$forceUpdate();
            },
            
            onPartsStatusChange: function(newStatus) {
                if (newStatus === 'ordered' && this.item.status !== 'scheduled') {
                    this.$bvToast.toast("Parts have been ordered. Consider updating the ticket status.", { 
                        title: "Info", 
                        variant: "info", 
                        autoHideDelay: 1500 
                    });
                } else if (newStatus === 'received') {
                    this.$bvToast.toast("Parts received! You can now proceed with the repair.", { 
                        title: "Success", 
                        variant: "success", 
                        autoHideDelay: 1500 
                    });
                }
            },
            
            onStatusChange: function(newStatus) {
                if (newStatus === 'scheduled') {
                    const currentDate = moment().format('MM-DD-YYYY');
                    this.item.scheduled_date = currentDate;
                    this.$bvToast.toast("Scheduled date set to today.", { 
                        title: "Info", 
                        variant: "info", 
                        autoHideDelay: 1500 
                    });
                }
            },
            
            onScheduledDateChange: function(newDate) {
                if (newDate && newDate.trim() !== '' && this.item.status !== 'scheduled') {
                    this.item.status = 'scheduled';
                    this.$bvToast.toast("Status changed to 'Scheduled'.", { 
                        title: "Info", 
                        variant: "info", 
                        autoHideDelay: 1500 
                    });
                }
            },
            
            getStatusClass: function(status) {
                const classes = {
                    new: 'status-new',
                    open: 'status-open',
                    assigned: 'status-assigned',
                    scheduled: 'status-scheduled',
                    resolved: 'status-resolved',
                    exchange: 'status-exchange',
                    denied: 'status-denied',
                    closed: 'status-closed',
                    pending: 'status-pending'
                };
                return classes[status] || 'status-default';
            },
            
            getStatusLabel: function(status) {
                const found = this.statuses.find(s => s.value === status);
                return found ? found.text : status;
            },
            
            getPartsStatusLabel: function(status) {
                const found = this.parts_statuses.find(s => s.value === status);
                return found ? found.text : status;
            },
            
            getPartsStatusClass: function(status) {
                const classes = {
                    in_stock: 'status-success',
                    need_to_order: 'status-warning',
                    ordered: 'status-info',
                    received: 'status-success'
                };
                return classes[status] || '';
            },
            
            formatDate: function(date) {
                if (!date) return null;
                return moment(date).format('MMM DD, YYYY h:mm A');
            },
            
            formatShortDate: function(date) {
                if (!date) return '';
                return moment(date).format('MMM DD');
            },
            
            showEmailStatus: function(emailResult) {
                if (emailResult && emailResult.email_sent) {
                    this.$bvToast.toast(
                        "Vendor notification email has been sent successfully.", 
                        { 
                            title: "Email Sent", 
                            variant: "success", 
                            autoHideDelay: 3000 
                        }
                    );
                } else if (emailResult && emailResult.email_error) {
                    this.$bvToast.toast(
                        "Failed to send vendor email: " + emailResult.email_error, 
                        { 
                            title: "Email Error", 
                            variant: "warning", 
                            autoHideDelay: 5000 
                        }
                    );
                }
            },
            
            save: function(e) {
                if (e) e.preventDefault();

                if (!this.item.invoice_id) {
                    this.$bvToast.toast("Please specify a valid invoice ID.", { title: "Error", variant: "warning" });
                    return;
                }

                if (!this.item.product_sku) {
                    this.$bvToast.toast("Please specify a valid product SKU.", { title: "Error", variant: "warning" });
                    return;
                }

                // Prepare parts_order JSON object
                let parts_order_obj = {
                    required: this.item.parts_required,
                    status: this.item.parts_status,
                    details: this.item.parts_details
                };
                
                let data = {
                    ...this.item,
                    parts_order: parts_order_obj  // Send as object, not string
                };
                
                // Debug: Log what's being sent
                console.log("Saving ticket with data:", {
                    parts_required: data.parts_required,
                    parts_status: data.parts_status,
                    parts_details: data.parts_details,
                    parts_order: data.parts_order
                });
                
                let that = this;
                let path = "/api/customer_service/save/"

                let s = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, { 
                            title: "Error", 
                            variant: "warning", 
                            autoHideDelay: 1500 
                        });
                    } else {
                        that.$bvToast.toast("Ticket saved successfully.", { 
                            title: "Success", 
                            variant: "success", 
                            autoHideDelay: 1500 
                        });
                        
                        // Show email status if applicable
                        if (response.data.email_sent) {
                            that.$bvToast.toast(
                                "Vendor notification email has been sent.", 
                                { 
                                    title: "Email Sent", 
                                    variant: "success", 
                                    autoHideDelay: 3000 
                                }
                            );
                        } else if (response.data.email_error) {
                            that.$bvToast.toast(
                                "Ticket saved but vendor email failed: " + response.data.email_error, 
                                { 
                                    title: "Email Warning", 
                                    variant: "warning", 
                                    autoHideDelay: 5000 
                                }
                            );
                        }
                        
                        that.$router.go();
                    }
                }
                let f = function(error) {
                    console.error("Save error:", error);
                    that.$bvToast.toast("Error saving ticket. Please try again.", { title: "Error", variant: "warning" });
                }
                Requestor.postSyncP(path, data, this, s, f);
            },
            
            fetch_init: function(id) {
                let path = "/api/customer_service/get_ticket/"
                let data = { id: id };
                let that = this;

                let s = function(response) {
                    if (response.data.result && response.data.result.length > 0) {
                        const fetchedItem = response.data.result[0];
                        
                        // Create a fully reactive object with all properties initialized
                        that.item = {
                            id: fetchedItem.id || null,
                            invoice_id: fetchedItem.invoice_id || null,
                            product_sku: fetchedItem.product_sku || null,
                            claim_date: fetchedItem.claim_date || null,
                            claim_detail: fetchedItem.claim_detail || null,
                            images: fetchedItem.images || [],
                            action: fetchedItem.action || null,
                            scheduled_date: fetchedItem.scheduled_date || null,
                            comments: fetchedItem.comments || null,
                            status: fetchedItem.status || "new",
                            fix_cost: fetchedItem.fix_cost || null,
                            created_on: fetchedItem.created_on || null,
                            updated_on: fetchedItem.updated_on || null,
                            assigned_to: fetchedItem.assigned_to || null,
                            status_history: fetchedItem.status_history || [],
                            parts_required: false,  // Default value
                            parts_status: null,
                            parts_details: null,
                            vendor_name: fetchedItem.vendor || null,
                            vendor_email: fetchedItem.vendor_email || null,
                            product_name: fetchedItem.product_name || null
                        };
                        
                        // Parse parts_order JSON if it exists
                        if (fetchedItem.parts_order) {
                            try {
                                let parts_data = typeof fetchedItem.parts_order === 'string' 
                                    ? JSON.parse(fetchedItem.parts_order) 
                                    : fetchedItem.parts_order;
                                
                                // Use Vue.set to ensure reactivity
                                that.$set(that.item, 'parts_required', parts_data.required || false);
                                that.$set(that.item, 'parts_status', parts_data.status || null);
                                that.$set(that.item, 'parts_details', parts_data.details || null);
                                
                                console.log('Parsed parts data:', parts_data);
                            } catch(e) {
                                console.error("Failed to parse parts_order:", e);
                            }
                        }
                        
                        // Force a re-render after loading
                        that.$forceUpdate();
                        
                    } else {
                        that.$bvToast.toast(response.data.error || "Error loading ticket.", { title: "Error", variant: "warning" });
                    }
                }
                let f = function(error) {
                    console.error("Fetch error:", error);
                    that.$bvToast.toast("Error loading ticket. Please try again.", { title: "Error", variant: "warning" });
                }
                Requestor.getSyncP(path, data, this, s, f);
            },
            
            contactVendor: function() {
                if (this.item.vendor_email) {
                    window.location.href = `mailto:${this.item.vendor_email}?subject=Parts%20Order%20for%20Ticket%20${this.item.id}&body=Regarding%20parts%20order%20for%20product%20${this.item.product_sku}`;
                }
            }
        },
        created: function() {
            if (this.viewdata && this.viewdata.id) {
                this.fetch_init(this.viewdata.id);
            } else {
                this.item.invoice_id = this.viewdata.invoice_id || null;
                this.item.product_sku = this.viewdata.product_sku || null;
                this.item.itemid = this.viewdata.itemid || null;
                this.newt = true;
            }
        }
    }
</script>

<style scoped>
/* Modal Width Styles - Apply to parent modal when this component is used in a modal */
.modal-dialog-centered.modal-dialog-scrollable .modal-content {
    max-height: none;
    width: 1609px;
}

.ticket-details-container {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #f8fafc;
    min-height: 100vh;
    padding: 24px;
}

/* Header */
.ticket-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 28px;
    flex-wrap: wrap;
    gap: 16px;
}

.ticket-id-section {
    display: flex;
    align-items: center;
    gap: 16px;
}

.ticket-badge {
    background: linear-gradient(135deg, #1e293b, #0f172a);
    padding: 8px 20px;
    border-radius: 48px;
    display: flex;
    align-items: center;
    gap: 10px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.badge-icon {
    font-size: 18px;
}

.badge-text {
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 1px;
    color: #94a3b8;
}

.badge-number {
    font-weight: 800;
    font-size: 18px;
    color: white;
    font-family: monospace;
}

.ticket-status-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    border-radius: 40px;
    font-size: 13px;
    font-weight: 600;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
}

.status-new { background: #eff6ff; color: #2563eb; }
.status-open { background: #fffbeb; color: #d97706; }
.status-assigned { background: #e0f2fe; color: #0284c7; }
.status-scheduled { background: #fef3c7; color: #b45309; }
.status-resolved { background: #d1fae5; color: #059669; }
.status-exchange { background: #ffedd5; color: #9a3412; }
.status-denied { background: #fee2e2; color: #dc2626; }
.status-closed { background: #f1f5f9; color: #475569; }
.status-pending { background: #fef9c3; color: #854d0e; }
.status-default { background: #f1f5f9; color: #334155; }

.save-button {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 24px;
    background: linear-gradient(135deg, #10b981, #059669);
    border: none;
    border-radius: 40px;
    font-weight: 600;
    font-size: 14px;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.save-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
}

/* Grid Layout */
.ticket-grid {
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 24px;
}

@media (max-width: 900px) {
    .ticket-grid {
        grid-template-columns: 1fr;
    }
}

/* Form Cards */
.form-card {
    background: white;
    border-radius: 24px;
    margin-bottom: 24px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
    transition: all 0.2s ease;
    border: 1px solid #eef2ff;
}

.form-card:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 24px;
    background: #fafcff;
    border-bottom: 1px solid #eef2ff;
}

.header-icon {
    font-size: 20px;
}

.card-header h3 {
    font-size: 16px;
    font-weight: 700;
    margin: 0;
    color: #0f172a;
    letter-spacing: -0.3px;
}

.card-body {
    padding: 24px;
}

/* Form Elements */
.form-row {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.form-group {
    flex: 1;
    min-width: 140px;
}

.form-group.full-width {
    width: 100%;
    flex: 100%;
}

.form-group.flex-2 {
    flex: 2;
}

label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 8px;
    letter-spacing: 0.3px;
}

.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.input-icon {
    position: absolute;
    left: 14px;
    font-size: 14px;
    opacity: 0.6;
}

.input-wrapper input,
.custom-select,
.custom-datepicker input,
textarea {
    width: 100%;
    padding: 0px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 14px;
    font-size: 13px;
    font-family: inherit;
    transition: all 0.2s ease;
    background: white;
}

.input-wrapper input {
    padding-left: 38px;
}

.input-wrapper input:focus,
.custom-select:focus,
textarea:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.custom-select {
    appearance: none;
    cursor: pointer;
}

.select-wrapper {
    position: relative;
}

.select-arrow {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #94a3b8;
}

textarea {
    padding: 12px 16px;
    resize: vertical;
    font-family: inherit;
}

.custom-datepicker {
    width: 100%;
}

.custom-datepicker input {
    padding: 12px 14px;
}

/* Radio Group Styles */
.radio-group {
    display: flex;
    gap: 20px;
    margin-top: 8px;
}

.radio-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: #334155;
}

.radio-label input[type="radio"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    margin: 0;
}

/* Vendor Info Card Styles */
.vendor-info-card {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    border-radius: 16px;
    padding: 16px;
    margin: 16px 0;
    border: 1px solid #bbf7d0;
}

.vendor-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #166534;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #bbf7d0;
}

.vendor-icon {
    font-size: 18px;
}

.vendor-details {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.vendor-row {
    display: flex;
    align-items: baseline;
    gap: 12px;
    font-size: 13px;
}

.vendor-label {
    min-width: 60px;
    font-weight: 600;
    color: #14532d;
}

.vendor-value {
    color: #1e293b;
    font-weight: 500;
}

.vendor-email-link {
    color: #059669;
    text-decoration: none;
    font-weight: 600;
    transition: color 0.2s;
}

.vendor-email-link:hover {
    color: #10b981;
    text-decoration: underline;
}

/* Parts Details Section */
.parts-details {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #eef2ff;
}

/* Alert Styles */
.alert-info,
.alert-warning,
.alert-success {
    display: flex;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 12px;
    margin-top: 16px;
}

.alert-info {
    background: #eff6ff;
    border-left: 3px solid #3b82f6;
}

.alert-warning {
    background: #fffbeb;
    border-left: 3px solid #f59e0b;
}

.alert-success {
    background: #f0fdf4;
    border-left: 3px solid #10b981;
}

.alert-icon {
    font-size: 20px;
}

.alert-content strong {
    display: block;
    font-size: 13px;
    margin-bottom: 4px;
    color: #1e293b;
}

.alert-content p {
    font-size: 12px;
    margin: 0;
    color: #475569;
}

/* Status color classes for parts */
.status-success {
    color: #059669;
    background: #d1fae5;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
}

.status-warning {
    color: #d97706;
    background: #fef3c7;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
}

.status-info {
    color: #0284c7;
    background: #e0f2fe;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
}

/* Image Gallery */
.gallery-section {
    margin-bottom: 24px;
}

.image-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(90px, 90px));
    gap: 12px;
}

.image-thumb {
    position: relative;
    width: 90px;
    height: 90px;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid #eef2ff;
    transition: all 0.2s;
}

.image-thumb:hover {
    transform: scale(1.02);
    border-color: #10b981;
}

.image-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.remove-image {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 20px;
    height: 20px;
    border-radius: 20px;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    border: none;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s;
}

.image-thumb:hover .remove-image {
    opacity: 1;
}

.empty-gallery {
    text-align: center;
    padding: 32px 20px;
    background: #f8fafc;
    border-radius: 20px;
    border: 1px dashed #cbd5e1;
}

.empty-icon {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.empty-gallery p {
    font-weight: 600;
    color: #475569;
    margin: 0 0 4px;
}

.empty-gallery span {
    font-size: 12px;
    color: #94a3b8;
}

.image-preview-card {
    margin-top: 20px;
    background: #f8fafc;
    border-radius: 16px;
    padding: 12px;
    text-align: center;
}

.preview-header {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 10px;
}

.preview-image {
    max-width: 100%;
    max-height: 200px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.upload-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    background: #f1f5f9;
    padding: 6px 14px;
    border-radius: 40px;
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    cursor: pointer;
    transition: all 0.2s;
}

.upload-button:hover {
    background: #e2e8f0;
}

/* Info Card */
.info-card .info-row {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    font-size: 13px;
}

.info-label {
    color: #64748b;
    font-weight: 500;
}

.info-value {
    font-weight: 600;
    color: #0f172a;
}

.divider {
    height: 1px;
    background: #eef2ff;
    margin: 16px 0;
}

.status-timeline {
    margin-top: 8px;
}

.timeline-title {
    font-size: 12px;
    font-weight: 700;
    color: #334155;
    margin-bottom: 12px;
}

.timeline-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    font-size: 12px;
}

.timeline-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #10b981;
}

.timeline-status {
    flex: 1;
    font-weight: 500;
    color: #334155;
}

.timeline-date {
    color: #94a3b8;
    font-size: 11px;
}

/* Contact Vendor Button */
.quick-action {
    margin-top: 8px;
}

.contact-vendor-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    cursor: pointer;
    transition: all 0.2s;
}

.contact-vendor-btn:hover {
    background: #e2e8f0;
    border-color: #10b981;
    color: #059669;
}

/* Animations */
@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

.form-card {
    animation: fadeIn 0.3s ease-out;
}

/* Responsive */
@media (max-width: 640px) {
    .ticket-details-container {
        padding: 16px;
    }
    
    .card-header {
        padding: 14px 20px;
    }
    
    .card-body {
        padding: 20px;
    }
    
    .form-row {
        flex-direction: column;
        gap: 16px;
    }
    
    .ticket-header {
        flex-direction: column;
        align-items: flex-start;
    }
    
    .save-button {
        width: 100%;
        justify-content: center;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/CustomerService/CustomerServiceTicket.vue